import React from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import routes from 'router/routes';
import { getAppointment, getServiceVisitDetailsPdfAsync } from 'api';

import {
  Breadcrumbs,
  ButtonLink,
  EmployeeBadge,
  InfoBox,
  InfoBoxItem,
  InfoBoxGroup,
  LabelMark,
  PageHeader,
  PaymentSummary,
  RichText,
  Group,
} from 'components/ui-kit';
import AttachedFormsList from 'components/attached-forms-list';
import { InvoiceSmallIcon } from 'assets/icons';

import { formatDateTime, formatDateTimeSpan } from 'helpers/datetime.helper';
import { appointmentStatusLabels, invoiceStatuses } from 'helpers/enums';

import { getColor, appointmentTypes, appointmentBusinessTypes } from 'constants/appointments.js';

import AppointmentLoader from './loader';

import { getAppointmentNotesLabel } from './utils';

import * as S from './styles';

const AppointmentItemDetails = ({
  breadcrumbs,
}) => {
  const { portalId, id } = useParams();
  const {
    isError, isLoading, isFetching, data,
  } = useQuery(
    ['appointment', portalId, id],
    getAppointment,
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!portalId,
    },
  );

  if (isError) {
    return 'Error occurred while fetching appointment';
  }

  if (isLoading || isFetching) {
    return <AppointmentLoader />;
  }

  if (!data) {
    return null;
  }

  const { data: appointment } = data;
  const {
    id: appointmentId,
    invoiceStatus,
    title,
    status,
    checkLists,
    serviceAddress,
    unitNumber,
    businessEntityType,
    employees,
    lineItems,
    taxRate,
    discount,
    appointmentNotes,
    servicePlanName,
    servicePlanDescription,
    scheduledStartTime,
    scheduledEndTime,
    isAllDay,
    actualStartTime,
    actualEndTime,
    preCalculatedSummary,
  } = appointment || {};

  const getDocumentButton = (entity) => {
    if ((entity.invoiceSent || invoiceStatus === invoiceStatuses.fullyPaid.value)
      && businessEntityType === appointmentBusinessTypes.job) {
      return (
        <ButtonLink
          to={`/${portalId}/${routes.invoices.path}/${entity.invoiceId}`}
          icon={<InvoiceSmallIcon />}
        >
          Invoice Details
        </ButtonLink>
      );
    }

    if (entity.wasEstimateSent) {
      return (
        <ButtonLink
          to={`/${portalId}/${routes.invoices.path}`}
          icon={<InvoiceSmallIcon />}
        >
          Estimate Details
        </ButtonLink>
      );
    }

    return null;
  };

  return (
    <S.AppointmentBox>
      {appointment && (
        <>
          <Breadcrumbs
            items={[
              ...breadcrumbs,
              { text: title },
            ]}
            gap="1rem"
          />

          <S.ContentBox>
            <PageHeader
              title={title}
              actionItems={getDocumentButton(appointment)}
            >
              <LabelMark
                text={appointmentTypes[businessEntityType]}
                color={getColor(businessEntityType)}
              />
            </PageHeader>

            <InfoBoxGroup>
              <InfoBox title="Appointment information">
                <InfoBoxItem
                  title="Property"
                  value={serviceAddress}
                  description={unitNumber ? `Unit #: ${unitNumber}` : ''}
                />

                {scheduledStartTime && (
                  <InfoBoxItem
                    title="Scheduled date"
                    // Both ends, so a customer whose job runs Tuesday to Thursday can see that. Reads as
                    // a closing time alone when the appointment starts and finishes on one day, and as
                    // dates plus "(Anytime)" when the record carries no meaningful clock times.
                    value={formatDateTimeSpan(scheduledStartTime, scheduledEndTime, isAllDay)}
                  />
                )}

                {actualStartTime && (
                  <InfoBoxItem
                    title="Start date"
                    value={formatDateTime(actualStartTime)}
                  />
                )}

                {actualEndTime && (
                  <InfoBoxItem
                    title="Finished date"
                    value={formatDateTime(actualEndTime)}
                  />
                )}

                <InfoBoxItem
                  title="Status"
                  value={appointmentStatusLabels[status]}
                />

                {!!servicePlanName && (
                  <>
                    <InfoBoxItem
                      title="Service plan"
                      value={servicePlanName}
                    />

                    <InfoBoxItem
                      title="Service Plan Details"
                      value={servicePlanDescription}
                      link={{
                        href: getServiceVisitDetailsPdfAsync(portalId, id),
                        text: 'Download visit details',
                      }}
                    />
                  </>
                )}

                {!!checkLists?.length && (
                  <AttachedFormsList
                    entityType={businessEntityType}
                    checkLists={checkLists}
                    eventId={appointmentId}
                  />
                )}
              </InfoBox>

              <InfoBox title="Employees">
                <S.Employees>
                  {employees.map((employee) => (
                    <EmployeeBadge
                      key={employee.id}
                      name={`${employee.firstName} ${employee.lastName}`}
                      phoneNumber={employee.phoneNumber}
                      isSmall={false}
                      imageUrl={employee.logoUrl}
                    />
                  ))}
                </S.Employees>
              </InfoBox>
            </InfoBoxGroup>

            <S.SummaryBox>
              <PaymentSummary
                lineItems={lineItems}
                taxRate={taxRate}
                discount={discount}
                preCalculatedSummary={preCalculatedSummary || {}}
                hideTotals={businessEntityType === appointmentBusinessTypes.visit}
                title={businessEntityType === appointmentBusinessTypes.visit ? '' : undefined}
              />
            </S.SummaryBox>

            {!!appointmentNotes && (
              <Group mt={40}>
                <InfoBoxItem
                  title={getAppointmentNotesLabel(businessEntityType)}
                  value={<RichText html={appointmentNotes} />}
                  showColon={false}
                />
              </Group>
            )}
          </S.ContentBox>
        </>
      )}
    </S.AppointmentBox>
  );
};

AppointmentItemDetails.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  breadcrumbs: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default AppointmentItemDetails;
